import React, { useState, useEffect } from 'react';
import { Info, Sparkles, Bug, ChevronRight, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';

// Configuração de versão - ATUALIZE AQUI A CADA DEPLOY
const VERSION_INFO = {
  version: '3.82.0',
  releaseDate: '19/09/2026',

  features: [
    {
      title: 'Nau IA — Consultor Inteligente de Suporte e Negócio',
      description: 'Novo assistente com botão flutuante em todas as telas: reconhece automaticamente em qual módulo você está e responde com o manual daquele contexto, sugere abrir chamado quando não sabe a resposta (sem inventar), permite avaliar cada resposta com 👍/👎 e tem seu próprio portal "Meus Chamados" com histórico e anexos.'
    },
    {
      title: 'Módulo de Chamados (Suporte)',
      description: 'Inbox central para o time de suporte com filtros em formato de "pills" (Período, Tipo, Status, Sentimento), painel lateral com histórico completo em chat, upload de anexos e dashboard clicável por status.'
    },
    {
      title: 'Central de Notificações Agendadas (Z-API/WhatsApp)',
      description: 'Relatórios recorrentes por WhatsApp (Saúde Financeira, Alertas de Produção/Estoque, Resumo Mensal) com matriz de destinatários por usuário ou papel, templates de mensagem editáveis e gatilhos financeiros automáticos (orçamento estourado, margem crítica).'
    },
    {
      title: 'Operações de Importação completas',
      description: 'Fluxo ponta a ponta Fornecedor → Produto → Cotação → Pedido com Landed Cost por fornecedor, taxa de câmbio travada na emissão, logística/frete e multimoeda em toda a jornada de Compras.'
    },
  ],

  fixes: [
    {
      title: 'Cronograma/Produção: fases ocultas por etapa macro',
      description: 'Corrigido o agrupamento de fases que ficava limitado a apenas 4 etapas macro, escondendo fases de etapas adicionais criadas em Configurações. Agora todas as etapas aparecem dinamicamente, e o número de ordem de cada fase é exibido ao lado do nome para evitar confusão entre fases parecidas.'
    },
    {
      title: 'Conclusão de Fase na Produção + progresso do projeto',
      description: 'Corrigido erro ao anexar foto na conclusão de uma fase, e o card "Progresso Geral" dos projetos agora reflete corretamente o cronograma de produção.'
    },
    {
      title: 'Upload de anexos em Compras (50MB, HEIC, OCR)',
      description: 'Limite de anexos unificado em 50MB com envio mais rápido e estável, suporte a fotos HEIC/HEIF do iPhone (convertidas automaticamente) e leitura por OCR de PDFs escaneados no Assistente de Compras.'
    },
  ],

  previousVersions: [
    {
      version: '3.75.1',
      date: '30/06/2026',
      highlights: ['Pedido unificado em abas (Detalhes/Financeiro/Anexos/Histórico)', 'Custo por Projeto no Assistente de Compras', 'Recebimento mais rápido', 'Pagamentos pago × em aberto', 'Aprovação item a item destravada']
    },
    {
      version: '3.74.0',
      date: '25/06/2026',
      highlights: ['Operações de Importação ponta a ponta', 'Landed Cost', 'Aprovação item a item', 'Desconto adicional no Pedido', 'Numeração atômica', 'Anexos de engenharia (DWG/STP)']
    },
    {
      version: '3.55.1',
      date: '14/05/2026',
      highlights: ['Edição de Propostas com Versionamento', 'Carimbo de Versão no PDF', 'Aprovação de Cotações com Limites', 'Filtros Avançados', 'Aba Financeiro do Pedido']
    },
    {
      version: '3.54.3',
      date: '07/05/2026',
      highlights: ['Histórico de Versões da Ficha', 'Diário de Produção em Timeline', 'Permissões por Perfil', 'Busca Inteligente no CRM', 'Modais com bloqueio acidental']
    },
    {
      version: '3.48.3',
      date: '07/04/2026',
      highlights: ['Certificação de Segurança', 'Logout com Revogação', 'Renovação de Sessão', 'SLA Dashboard', '9 PDFs Técnicos']
    },
    {
      version: '3.32.0',
      date: '23/03/2026',
      highlights: ['Dashboard 360', 'Icones de Ajuda', 'Toggle Grade/Lista', 'Nomenclaturas Padronizadas']
    },
    {
      version: '3.28.0',
      date: '20/03/2026',
      highlights: ['Supplier Score', 'Configuracao de Pesos', 'Gantt de Projetos', 'Kanban Melhorado']
    },
    {
      version: '3.26.0',
      date: '13/03/2026',
      highlights: ['Criar Projeto do Pipeline', 'Embarcacoes automaticas', 'PDF auto-anexado', 'Excluir Propostas']
    }
  ]
};

const VersionBadge = () => {
  const [showChangelog, setShowChangelog] = useState(false);

  // Auto-abre o changelog na PRIMEIRA vez que o usuario entrar nesta versao.
  // Usa localStorage por usuario/navegador. Marca como visto so quando ele fechar
  // — assim ele ve sem precisar receber via WhatsApp.
  useEffect(() => {
    try {
      const lastSeen = localStorage.getItem('nautilus_last_seen_version');
      if (lastSeen !== VERSION_INFO.version) {
        // pequeno delay para nao competir com o toast de "Login realizado"
        const t = setTimeout(() => setShowChangelog(true), 800);
        return () => clearTimeout(t);
      }
    } catch (e) {
      // localStorage indisponivel (ex: modo privado) — ignora silenciosamente
    }
  }, []);

  const handleOpenChange = (open) => {
    setShowChangelog(open);
    if (!open) {
      // Marca esta versao como vista quando o usuario fechar o modal
      try {
        localStorage.setItem('nautilus_last_seen_version', VERSION_INFO.version);
      } catch (e) { /* silent */ }
    }
  };

  return (
    <>
      {/* Badge de versão clicável */}
      <button
        onClick={() => setShowChangelog(true)}
        className="flex items-center gap-1.5 px-2 py-1 rounded-md bg-blue-600/20 hover:bg-blue-600/30 transition-colors text-blue-100 text-xs"
        title="Ver novidades da versão"
        data-testid="version-badge-btn"
      >
        <Info size={12} />
        <span>v{VERSION_INFO.version}</span>
      </button>

      {/* Modal de Changelog */}
      <Dialog open={showChangelog} onOpenChange={handleOpenChange}>
        <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="version-changelog-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <div className="bg-blue-600 p-2 rounded-lg">
                <Sparkles className="h-5 w-5 text-white" />
              </div>
              <div>
                <span>Nautilus OS Enterprise</span>
                <Badge variant="secondary" className="ml-2">v{VERSION_INFO.version}</Badge>
              </div>
            </DialogTitle>
          </DialogHeader>

          <ScrollArea className="max-h-[60vh] pr-4">
            <div className="space-y-6">
              {/* Data de lançamento */}
              <p className="text-sm text-gray-500">
                Lançado em {VERSION_INFO.releaseDate}
              </p>

              {/* Novas Funcionalidades */}
              {VERSION_INFO.features.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-green-600 mb-3">
                    <Sparkles size={20} />
                    Novidades
                  </h3>
                  <div className="space-y-3">
                    {VERSION_INFO.features.map((feature, index) => (
                      <div key={index} className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <h4 className="font-medium text-green-800 flex items-center gap-2">
                          <ChevronRight size={16} />
                          {feature.title}
                        </h4>
                        <p className="text-sm text-green-700 mt-1 ml-6">
                          {feature.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Correções */}
              {VERSION_INFO.fixes.length > 0 && (
                <div>
                  <h3 className="flex items-center gap-2 text-lg font-semibold text-orange-600 mb-3">
                    <Bug size={20} />
                    Correções
                  </h3>
                  <div className="space-y-3">
                    {VERSION_INFO.fixes.map((fix, index) => (
                      <div key={index} className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                        <h4 className="font-medium text-orange-800 flex items-center gap-2">
                          <ChevronRight size={16} />
                          {fix.title}
                        </h4>
                        <p className="text-sm text-orange-700 mt-1 ml-6">
                          {fix.description}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </ScrollArea>

          <div className="flex justify-end pt-4 border-t">
            <Button variant="outline" onClick={() => setShowChangelog(false)}>
              Fechar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VersionBadge;
